import { useState } from 'react';
import flatten from 'lodash/flatten';
import {
  FormState,
  ValidationResult,
  parseValidationResults,
  useExtendedReducer
} from '@portal/ui-library';

import { ApiError, Application, ApplicationService } from 'models/extension-generated';
import { useWizardNavigation } from '../useWizardNavigation';
import { RecursiveKeyOf } from '@portal/ui-library/dist/models';
import { WizardRoutesEnum } from 'utils/actions';
import { getSchemaForRoute } from './pages/getSchemaForRoute';

/**
 * Hook for auto triggering of the async validation
 */
export const useApplicationValidation = (
  state: FormState<Application>,
  reducer: ReturnType<typeof useExtendedReducer>,
  partnerId?: string
) => {
  const [isValidating, setIsValidating] = useState(false);
  const { ScreenOrder } = useWizardNavigation();

  const { setValidationResults, setDirty, addValidationResults } = reducer;

  /**
   * Calls BE service to get the validation results
   */
  async function validateAsync(
    validatedFields: Array<RecursiveKeyOf<Application>>,
    keepPreviousErrors: boolean
  ): Promise<Map<string, ValidationResult>> {
    setIsValidating(true);

    try {
      await ApplicationService.validatePublicApplication({ requestBody: state.model });
      const emptyValidationResult = new Map<string, ValidationResult>();

      setValidationResults({ results: emptyValidationResult });

      return emptyValidationResult;
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.body?.errors.length) {
          const errors = error.body.errors;
          const results = parseValidationResults(errors, validatedFields, state);
          if (keepPreviousErrors) {
            addValidationResults({ results });
          } else {
            setValidationResults({ results });
          }
          return results;
        }
      }
    } finally {
      setIsValidating(false);
    }
    return new Map<string, ValidationResult>();
  }

  async function validateNextPage(nextPage: WizardRoutesEnum) {
    const indexOfTargetPage = ScreenOrder.indexOf(nextPage);
    const priorPages = ScreenOrder.slice(0, indexOfTargetPage);
    const shouldValidateAllPartners = nextPage === WizardRoutesEnum.InsuranceHolder;

    if (indexOfTargetPage === -1) {
      // validation of the last page where all pages get validated
      const validation = await validatePages({
        pages: ScreenOrder
      });

      return validation;
    } else {
      const validation = await validatePages({
        pages: priorPages,
        currentPartnerId: shouldValidateAllPartners ? undefined : partnerId
      });

      return validation;
    }
  }

  async function validatePartner(partnerId: string) {
    const validation = await validatePages({
      pages: [WizardRoutesEnum.PrivateData]
    });

    return validation;
  }

  /*
   * Validate minimal viable partner that can be stored
   */
  async function validatePages({
    pages,
    currentPartnerId
  }: {
    pages: Array<WizardRoutesEnum>;
    currentPartnerId?: string;
  }): Promise<Map<string, ValidationResult>> {
    const fieldsToValidate = flatten(
      pages.map((page) => {
        return getSchemaForRoute(page, state.model, currentPartnerId);
      })
    );

    fieldsToValidate.forEach(setDirty);

    return validateAsync(fieldsToValidate, false);
  }

  return {
    isValidating,
    validatePartner,
    validateNextPage
  };
};
